import { FileSpreadsheet, FileText, Filter, Home, ListChecks, MapPin, Search, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { ApiRequestError } from "../api";
import { loadAuthorizedReportData, type WebReportData } from "../features/reports/report-web-data";
import { REPORT_DEFINITIONS } from "../features/desktop-add-new-data/modules/reports/report-calculations";
import {
  getReportCategory,
  getReportSearchText,
  REPORT_CATEGORIES,
  type ReportCategory,
} from "../features/desktop-add-new-data/modules/reports/report-ui";

const allCategories = "All categories";

export function ReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<WebReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<ReportCategory | typeof allCategories>(allCategories);
  const canUseMunicipalityScope = user?.role === "MUNICIPAL_CITY_USER" || user?.role === "MUNICIPAL_CITY_ADMINISTRATOR";
  const [municipalityWide, setMunicipalityWide] = useState(canUseMunicipalityScope && searchParams.get("scope") === "municipality");
  const [selectedBarangay, setSelectedBarangay] = useState(searchParams.get("barangay") ?? "");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    loadAuthorizedReportData()
      .then((result) => {
        if (!active) return;
        setData(result);
        if (!selectedBarangay && result.barangays.length) setSelectedBarangay(result.barangays[0].name);
        if (!canUseMunicipalityScope) setMunicipalityWide(false);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError instanceof ApiRequestError ? requestError.message : "Submitted report data could not be loaded from the RBIM server.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const municipality = canUseMunicipalityScope ? municipalityName(user?.locationLabel ?? "") : "";
  const currentScope = municipalityWide ? municipality || "Municipality-wide" : selectedBarangay || "Select barangay";
  const residentCount = data?.residents.length ?? 0;
  const householdCount = data?.households.length ?? 0;
  const normalizedSearch = search.trim().toLowerCase();
  const filteredReports = useMemo(() => REPORT_DEFINITIONS.filter((definition) => {
    const matchesCategory = category === allCategories || getReportCategory(definition.id) === category;
    const matchesSearch = !normalizedSearch || getReportSearchText(definition).includes(normalizedSearch);
    return matchesCategory && matchesSearch;
  }), [category, normalizedSearch]);

  function openReport(reportId: number) {
    const params = new URLSearchParams();
    if (municipality) params.set("municipality", municipality);
    if (municipalityWide) params.set("scope", "municipality");
    else {
      params.set("scope", "barangay");
      if (selectedBarangay) params.set("barangay", selectedBarangay);
    }
    navigate(`/reports/${reportId}?${params.toString()}`);
  }

  return <section className="desktop-reports-page">
    <header className="desktop-report-hero">
      <div className="desktop-report-hero-icon"><FileSpreadsheet /></div>
      <div><span>STATISTICAL TABLES</span><h1>Reports</h1><p>Generate RBIM statistical tables from submitted resident and household records.</p></div>
    </header>

    {error ? <div className="form-error" role="alert">{error}</div> : null}

    <section className="desktop-report-summary-grid">
      <ReportSummaryCard icon={<Users />} label="Submitted Residents" value={loading ? "—" : residentCount.toLocaleString()} helper="Finalized resident records" />
      <ReportSummaryCard icon={<Home />} label="Submitted Households" value={loading ? "—" : householdCount.toLocaleString()} helper="Finalized household records" />
      <ReportSummaryCard icon={<ListChecks />} label="Available Reports" value={REPORT_DEFINITIONS.length.toString()} helper="RBIM statistical tables" />
      <ReportSummaryCard icon={<MapPin />} label="Current Scope" value={currentScope} helper={municipalityWide ? "Municipality-wide" : "Barangay scope"} />
    </section>

    <section className="desktop-report-controls-card">
      <div className="desktop-report-controls-top">
        <div><strong>REPORT CONTROLS</strong><p>Choose a scope, then open a report table to view and export it.</p></div>
        <div className="desktop-report-actions">
          <button type="button" className="desktop-report-pdf" onClick={() => setFeedback("Please open a report table first.")}><FileText />PDF</button>
          <button type="button" className="desktop-report-excel" onClick={() => setFeedback("Please open a report table first.")}><FileSpreadsheet />Excel</button>
          {canUseMunicipalityScope ? <label className="desktop-report-check"><input type="checkbox" checked={municipalityWide} onChange={(event) => setMunicipalityWide(event.currentTarget.checked)} />Municipality-wide</label> : null}
          <select disabled={municipalityWide || !canUseMunicipalityScope} value={selectedBarangay} onChange={(event) => setSelectedBarangay(event.currentTarget.value)}>
            <option value="">Select barangay</option>
            {data?.barangays.map((barangay) => <option key={barangay.id} value={barangay.name}>{barangay.name}</option>)}
          </select>
        </div>
      </div>
      <div className="desktop-report-filter-row">
        <label className="desktop-report-search"><Search /><input type="search" value={search} onChange={(event) => setSearch(event.currentTarget.value)} placeholder="Search table number, title, or category" /></label>
        <label className="desktop-report-category"><Filter /><select value={category} onChange={(event) => setCategory(event.currentTarget.value as ReportCategory | typeof allCategories)}><option>{allCategories}</option>{REPORT_CATEGORIES.map((item) => <option key={item.label}>{item.label}</option>)}</select></label>
      </div>
    </section>

    {feedback ? <div className="desktop-report-feedback">{feedback}</div> : null}
    {!loading && !error && residentCount === 0 && householdCount === 0 ? <div className="desktop-report-empty">No submitted RBIM records yet. Submit completed Add New Data records first to generate reports.</div> : null}

    <section className="desktop-report-catalog">
      <div className="desktop-report-catalog-heading"><div><h2>REPORTS</h2><p>Choose one of the 42 RBIM statistical tables.</p></div><div><span>{REPORT_DEFINITIONS.length} Tables</span><span>{filteredReports.length} shown</span></div></div>
      <div className="desktop-report-options">
        {filteredReports.map((definition) => <button type="button" key={definition.id} className="desktop-report-option" onClick={() => openReport(definition.id)}>
          <div className="desktop-report-option-top"><span>TABLE {definition.id}</span><small>{getReportCategory(definition.id)}</small></div>
          <strong>{definition.title}</strong>
        </button>)}
        {!filteredReports.length ? <div className="desktop-report-no-match">No report tables match the current search and category filter.</div> : null}
      </div>
    </section>
  </section>;
}

function ReportSummaryCard({ icon, label, value, helper }: { icon: React.ReactNode; label: string; value: string; helper: string }) {
  return <article className="desktop-report-summary-card"><div className="desktop-report-summary-icon">{icon}</div><div><span>{label}</span><strong>{value}</strong><p>{helper}</p></div></article>;
}

function municipalityName(locationLabel: string) {
  return locationLabel.split(",")[0]?.trim() ?? "";
}
